// -----------------------------------------------------------------------
// <copyright company="Microsoft Corporation">
// Copyright (c) Microsoft Corporation. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import type { ICommand, IMessenger, INotification, IResult } from '@ms/utilities-cross-window';
import { createHost } from '@ms/utilities-cross-window';

import { v4 as guid } from 'uuid';
import type { IDisposable, ITokenRetriever, SupportedPlaybackSpeed } from '../../types/external';
import type { IIframeApi, OnPreSessionErrorCallback } from '../../types/internal';
import { IframeApi } from '../iFrameApi';
import type { OnePlayerError } from '../onePlayerError';

export interface IHost extends IMessenger, IDisposable {
  dispose: () => void;
}
export type OnNotification = (notification: IAllPossibleNotifications) => void;
export type OnCommand = (command: ICommand) => Promise<IResult> | undefined;

// This interface is duplicated in clientWrapper.ts for stream-embed because we currently
// Do not have shared typings between the two packages yet (tracked by PBI #1685541). If you update
// this interface, please make sure to also update the interface in clientWrapper.ts.
export interface IAllPossibleNotifications extends INotification {
  isReady?: boolean;
  playState?: string;
  ttlMs?: number;
  ttirMs?: number;
  timestamp?: number;
  timestampTimeOrigin?: number;
  error?: Error;
  onePlayerError?: OnePlayerError;
  pbsid?: string; // stands for playbackSessionId
  hsi?: string; // stands for hostSessionId
  currentTime?: number;
  isMuted?: boolean;
  volume?: number;
  durationInSeconds?: number;
  playbackSpeed?: SupportedPlaybackSpeed;
  title?: string;
  targetTimeInSeconds?: number;
  itemUrl?: string;
  value?: string;
}

/*
 * Used for cross window communication between the host (OnePlayer iFrame API) and client (either
 * stream-embed or odsp-embed, both are using the same communication protocol)
 */
export class HostWrapper {
  public iFrameApi!: IIframeApi;

  private readonly _host: IHost;
  private readonly _channelId = guid();
  private readonly _embedUrl: string;
  private _onNotificationCallback!: OnNotification;
  private _onCommandCallback!: OnCommand;
  private readonly _onError: OnPreSessionErrorCallback;
  private readonly _onTitleOrDisplayNameUpdate: (value: string | undefined) => void;
  private readonly _tokenRetriever: ITokenRetriever;

  public constructor(
    embedUrl: string,
    iframe: HTMLIFrameElement,
    tokenRetriever: ITokenRetriever,
    onError: OnPreSessionErrorCallback,
    onTitleOrDisplayNameUpdate: (value: string | undefined) => void
  ) {
    this._onError = onError;
    this._onTitleOrDisplayNameUpdate = onTitleOrDisplayNameUpdate;

    if (iframe.contentWindow === null) {
      throw new Error("The iframe's contentWindow is null");
    }

    // If we are hosted in a sourceless iframe, the origin will be "null" and so we need to tell the client
    // that any origin will do, since the postMessage() call from the client to us will fail if it tries to restrict
    // the target origin of the message to "null"
    const origin = window.location.origin && window.location.origin !== 'null' ? window.location.origin : '*';
    const newEmbedUrl: URL = new URL(embedUrl);
    newEmbedUrl.hash = '#channelId=' + this._channelId + '&origin=' + origin;
    this._embedUrl = newEmbedUrl.toString();
    this._host = createHost({
      identifyParent: true,
      source: iframe.contentWindow /* Required to be able to identify itself to client */,
      channelId: this._channelId.toString(),
      origin: new URL(embedUrl).origin,
      initTimeoutMs: 40000 /* Matches Embed.aspx initTimeoutMs */,
      onCommand: this.onCommand.bind(this),
      onNotification: this.onNotification.bind(this)
    });
    this._tokenRetriever = tokenRetriever;
  }

  public createIframeApi(): void {
    this.iFrameApi = new IframeApi(
      this._host,
      this._tokenRetriever,
      this._embedUrl,
      this._onError,
      this._onTitleOrDisplayNameUpdate
    );
    this._onNotificationCallback = this.iFrameApi.onNotification.bind(this.iFrameApi);
    this._onCommandCallback = this.iFrameApi.onCommand.bind(this.iFrameApi);
  }

  public reconnectHost(existingIframeApi: IIframeApi): void {
    existingIframeApi.crossWindow = this._host;
    this.iFrameApi = existingIframeApi;
    this._onNotificationCallback = this.iFrameApi.onNotification.bind(this.iFrameApi);
    this._onCommandCallback = this.iFrameApi.onCommand.bind(this.iFrameApi);
  }

  public getDecoratedEmbedUrl(): string {
    return this._embedUrl;
  }

  public dispose(): void {
    this._host.dispose();
  }

  private onCommand(command: ICommand): Promise<IResult> | undefined {
    return this._onCommandCallback(command);
  }

  private onNotification(notification: IAllPossibleNotifications): void {
    this._onNotificationCallback(notification);
  }
}
